import { useState, useRef, useCallback } from 'react';
import { useUser } from '@clerk/react';
import { useAuthStore } from '@/store/auth-store.js';
import { axiosInstance } from '@edison/api-client-react';
import {
  Sparkles,
  ArrowUp,
  FolderPlus,
  FileText,
  PackageCheck,
} from 'lucide-react';
import { useLocation, Link } from 'wouter';
import { EdisonMark } from '@/components/icons/edison-logo.js';
import { FilterChip } from '@/components/ui/filter-chip.js';
import { AnsweredState } from './answered.js';
import type { AIStep } from '@/components/ui/ai-stepper.js';

// ─── Types ────────────────────────────────────────────────────────────────────



// ─── Constants ────────────────────────────────────────────────────────────────

const CURRENT_FY = 'FY2026';

const INTENT_MODES = [
  { key: 'ask', label: 'Ask' },
  { key: 'new_project', label: 'New project' },
  { key: 'draft_claim', label: 'Draft claim' },
  { key: 'audit_pack', label: 'Audit pack' },
] as const;

type IntentMode = typeof INTENT_MODES[number]['key'];

const INTENT_PLACEHOLDER: Record<IntentMode, string> = {
  ask: 'Ask about your R&D claim, evidence, or eligibility…',
  new_project: 'Describe the new R&D project…',
  draft_claim: 'Which income year should I draft a claim for?',
  audit_pack: 'Which project or claim do you want an audit pack for?',
};

const ACTION_TILES = [
  {
    icon: FolderPlus,
    title: 'Start a new R&D project',
    subtitle: 'Set up a project and begin capturing evidence.',
    action: { type: 'nav' as const, href: '/projects/new' },
  },
  {
    icon: FileText,
    title: 'Browse grant opportunities',
    subtitle: 'Find open grants and generate a draft application from your evidence.',
    action: { type: 'nav' as const, href: '/grants' },
  },
  {
    icon: PackageCheck,
    title: `Prepare the ${CURRENT_FY} audit pack`,
    subtitle: 'Review readiness and export evidence for auditors.',
    action: { type: 'nav' as const, href: '/audit-readiness' },
  },
];

const MANAGER_SUGGESTIONS = [
  `What is blocking the ${CURRENT_FY} claim?`,
  'Where is our R&D effort going this month?',
  'Which projects have outstanding TFA?',
  `What is our estimated R&D expenditure this FY?`,
];

const DEVELOPER_SUGGESTIONS = [
  'Which of my time entries were classified as non-R&D last month?',
  'What activities need my competence confirmed?',
  'Show me evidence linked to my recent commits.',
  'How many R&D hours have I logged this week?',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function formatTodayLong(): string {
  return new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function currentIncomeYear(): string {
  const now = new Date();
  const fy = now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
  return `FY${fy}`;
}

// ─── HomePage ─────────────────────────────────────────────────────────────────

export function HomePage() {
  const { user: clerkUser } = useUser();
  const { user } = useAuthStore();
  const [, navigate] = useLocation();

  const isDeveloper = user?.role === 'member';
  const suggestions = isDeveloper ? DEVELOPER_SUGGESTIONS : MANAGER_SUGGESTIONS;
  const firstName = user?.name?.split(' ')[0] ?? clerkUser?.firstName ?? null;

  const [input, setInput] = useState('');
  const [intentMode, setIntentMode] = useState<IntentMode>('ask');
  const [answeredQuestion, setAnsweredQuestion] = useState<string | null>(null);
  const [answeredContent, setAnsweredContent] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const msg = input.trim();
    if (!msg) return;

    if (intentMode === 'new_project') {
      navigate('/projects/new');
      return;
    }
    if (intentMode === 'draft_claim') {
      navigate('/claims/new');
      return;
    }
    if (intentMode === 'audit_pack') {
      navigate('/audit-readiness');
      return;
    }

    setAnsweredQuestion(msg);
    setInput('');
    setIsStreaming(true);
    setAnsweredContent(null);

    try {
      const res = await axiosInstance.post<{ data: { message: string } }>('/api/home/ask', {
        question: msg,
      });
      const reply = res.data?.data?.message ?? "I'm not sure — try rephrasing your question.";
      setAnsweredContent(reply);
    } catch {
      setAnsweredContent('Unable to reach Edison right now. Please try again.');
    } finally {
      setIsStreaming(false);
    }
  }, [input, intentMode, navigate]);

  const aiSteps: AIStep[] = [
    { key: 'search', label: 'Searching evidence', status: isStreaming ? 'active' : 'done' },
    { key: 'analyse', label: 'Analysing claim position', status: 'pending' },
    { key: 'compose', label: 'Composing answer', status: 'pending' },
  ];

  if (answeredQuestion !== null) {
    return (
      <div
        className="flex flex-col"
        style={{ height: '100%', animation: 'pagein 0.3s cubic-bezier(0.16,1,0.3,1) both' }}
      >
        <AnsweredState
          question={answeredQuestion}
          answer={answeredContent}
          isStreaming={isStreaming}
          steps={aiSteps}
          onBack={() => {
            setAnsweredQuestion(null);
            setAnsweredContent(null);
            setIsStreaming(false);
          }}
        />
      </div>
    );
  }

  return (
    <div
      className="flex flex-col overflow-y-auto"
      style={{ height: '100%', animation: 'pagein 0.3s cubic-bezier(0.16,1,0.3,1) both' }}
    >
      <div
        className="flex-1 flex flex-col items-center justify-center px-8 py-10 min-h-0"
        style={{ background: 'radial-gradient(120% 80% at 50% -10%, oklch(0.97 0.012 262), oklch(0.98 0.004 250) 55%)' }}
      >
        <div className="mb-8 flex flex-col items-center gap-3">
          <div
            className="flex shrink-0 items-center justify-center rounded-[14px]"
            style={{
              height: 56,
              width: 56,
              background: '#0072D5',
              boxShadow: '0 12px 40px oklch(0.45 0.14 250 / 0.28)',
            }}
          >
            <EdisonMark size={28} />
          </div>
          <div className="text-center">
            <p className="text-[22px] font-semibold tracking-tight text-foreground leading-tight">
              {getGreeting()}{firstName ? `, ${firstName}` : ''}.
            </p>
            <p className="text-sm text-muted-foreground mt-0.5">
              {formatTodayLong()} · {currentIncomeYear()}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap justify-center">
          {INTENT_MODES.map(m => (
            <FilterChip
              key={m.key}
              label={m.label}
              active={intentMode === m.key}
              onClick={() => setIntentMode(m.key)}
            />
          ))}
        </div>

        <form onSubmit={handleSubmit} className="w-full max-w-[640px] mb-6">
          <div
            className="flex items-center gap-3 rounded-[14px] border border-border bg-card px-4 py-3"
            style={{ boxShadow: '0 4px 20px oklch(0.2 0.03 260 / 0.08)' }}
          >
            <Sparkles className="h-4 w-4 text-[oklch(0.44_0.09_262)] shrink-0" aria-hidden="true" />
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={INTENT_PLACEHOLDER[intentMode]}
              className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
              aria-label="Ask Edison"
            />
            <button
              type="submit"
              disabled={!input.trim() || isStreaming}
              className="h-8 w-8 rounded-[9px] bg-[oklch(0.28_0.05_255)] text-white flex items-center justify-center disabled:opacity-40 transition-opacity"
              aria-label="Submit"
            >
              <ArrowUp className="h-4 w-4" />
            </button>
          </div>
        </form>

        <div className="flex flex-wrap items-center justify-center gap-2 mb-10 max-w-[640px]">
          {suggestions.map(s => (
            <button
              key={s}
              type="button"
              onClick={() => { setInput(s); inputRef.current?.focus(); }}
              className="px-3 py-1.5 rounded-[20px] border border-border bg-card text-xs text-muted-foreground hover:text-foreground hover:border-[oklch(0.80_0.006_250)] transition-colors"
            >
              {s}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full max-w-[640px]">
          {ACTION_TILES.map(tile => (
            <Link key={tile.title} href={tile.action.href}>
              <div className="rounded-[14px] border border-border bg-card px-4 py-4 hover:shadow-md transition-all cursor-pointer">
                <tile.icon className="h-5 w-5 text-[oklch(0.44_0.09_262)] mb-2" aria-hidden="true" />
                <p className="text-sm font-medium text-foreground">{tile.title}</p>
                <p className="text-xs text-muted-foreground mt-1 leading-snug">{tile.subtitle}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
